/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Our Story content page.
 *
 * RENDERED VIA THE CONTENT ROUTE, NOT A ROUTE FILE. baggallini serves single-segment `.html`
 * URLs (`/our-story.html`) from `_app.$contentId[.html].tsx`, which owns that entire URL shape
 * (see W-23754187). A literal `_app.our-story[.html].tsx` route can't win React Router's ranking
 * against `/:contentId.html`, so this page is exposed as a plain component that the content route
 * delegates to when `contentId === 'our-story'`. See `CUSTOM_CONTENT_PAGES` there.
 *
 * The server data-load (`loadOurStoryData`) and the `@PageType`/`@RegionDefinition` authoring
 * metadata live in the sibling `./our-story-page.data.ts` (keeps this file exporting only a
 * component for fast refresh); `generate-cartridge` still discovers the metadata there.
 */
import type { ReactElement } from 'react';
import jetSetImage from '/images/jet-set.jpg';
import organizedByDesignImage from '/images/organized-by-design.jpg';
import { Link } from '@/components/link';
import {
    Breadcrumb,
    BreadcrumbList,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { SeoMeta } from '@/components/seo-meta';
import { Region } from '@/components/region';
import { CategoryHrefProvider, useCategoryHref } from '@/components/navigation-menu/category-href';
import type { OurStoryPageData } from './our-story-page.data';

const PILLARS = [
    {
        title: 'Organized by Design',
        body: 'Every Baggallini bag starts with a simple question: what do you actually need to carry? Our signature multi-pocket layouts are built around real life — so your phone, keys, cards, and everything else has a home.',
        img: organizedByDesignImage,
    },
    {
        title: 'Lightweight & Durable',
        body: 'We use high-quality nylon and innovative materials to keep our bags light without sacrificing structure. Many customers carry the same Baggallini bag for four or more years.',
        img: jetSetImage,
    },
    {
        title: 'Security You Can Count On',
        body: 'Our Securtex® anti-theft collection features slash-resistant straps, locking zippers, RFID-blocking pockets, and water-resistant materials — everything you need to move through the world with confidence.',
        img: `https://www.baggallini.com/dw/image/v2/BJGK_PRD/on/demandware.static/-/Sites-baggalini-Library/default/dw43ee9b7c/fa26/7.7HP/BG-20260706-SUB2-IMAGE3-D.jpg`,
    },
];

function OurStoryContent() {
    const rootHref = useCategoryHref()('root');
    return (
        <>
            {/* Hero */}
            <div className="bg-[#F5F3F0] flex flex-col items-center justify-center py-16 sm:py-24 text-center px-6">
                <h1
                    style={{ fontFamily: 'var(--font-display)' }}
                    className="text-6xl sm:text-8xl font-light tracking-wide text-[#1A1A1A] mb-4">
                    Our Story
                </h1>
                <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed">
                    Baggallini creates organized bags that simplify life — so women can move through the world feeling
                    confident and in control.
                </p>
            </div>

            {/* Mission statement */}
            <div className="max-w-3xl mx-auto px-6 py-16 text-center">
                <p
                    style={{ fontFamily: 'var(--font-display)' }}
                    className="text-3xl lg:text-4xl font-light text-[#1A1A1A] leading-relaxed mb-6">
                    &quot;When you&apos;re prepared, you&apos;re confident.&quot;
                </p>
                <p className="text-base text-muted-foreground leading-relaxed">
                    That belief sits at the heart of every Baggallini bag. We design for women who are always on the go
                    — whether commuting through the city, navigating airport security, or exploring somewhere new. Our
                    bags are built to keep up.
                </p>
            </div>

            {/* Brand pillars */}
            <div className="max-w-screen-xl mx-auto px-6 pb-24 space-y-0">
                {PILLARS.map((item, i) => {
                    const isEven = i % 2 === 0;
                    return (
                        <div
                            key={item.title}
                            className={`flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} items-center`}>
                            <div className="w-full md:w-1/2 aspect-[4/3] overflow-hidden">
                                <img src={item.img} alt={item.title} className="w-full h-full object-cover" />
                            </div>
                            <div
                                className={`w-full md:w-1/2 px-8 py-12 md:py-16 ${isEven ? 'bg-[#F5F3F0]' : 'bg-background'}`}>
                                <h2
                                    style={{ fontFamily: 'var(--font-display)' }}
                                    className="text-4xl font-light mb-4 text-[#1A1A1A]">
                                    {item.title}
                                </h2>
                                <p className="text-base text-muted-foreground leading-relaxed mb-6">{item.body}</p>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Collections CTA */}
            <div className="bg-[#1A1A1A] py-16 text-center text-primary-foreground px-6">
                <h2 style={{ fontFamily: 'var(--font-display)' }} className="text-5xl font-light mb-4">
                    Find Your Perfect Bag
                </h2>
                <p className="text-primary-foreground/70 mb-8 max-w-xl mx-auto">
                    From everyday crossbodies to travel-ready totes, there&apos;s a Baggallini built for the way you
                    live.
                </p>
                <Link
                    to={rootHref}
                    className="inline-flex items-center gap-3 bg-background text-[#1A1A1A] text-sm tracking-[0.12em] uppercase font-medium px-9 py-4 hover:bg-background/90 transition-colors rounded-none">
                    Shop All Bags
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                </Link>
            </div>
        </>
    );
}

export function OurStoryPage({ loaderData }: { loaderData: OurStoryPageData }): ReactElement {
    return (
        <CategoryHrefProvider value={loaderData.categoryUrls}>
            <div className="pb-8">
                <SeoMeta
                    title="About Baggallini — Our Story"
                    description="Learn about Baggallini — organized bags designed to simplify life so women can move through the world feeling confident and in control."
                    openGraph={{ type: 'article', url: loaderData.pageUrl, image: loaderData.ogImageUrl }}
                />

                <div className="max-w-screen-2xl mx-auto px-4 pt-4 pb-2">
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem>
                                <BreadcrumbLink asChild>
                                    <Link to="/">Home</Link>
                                </BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem>
                                <BreadcrumbPage>Our Story</BreadcrumbPage>
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>
                </div>

                {loaderData.page ? (
                    <Region page={loaderData.page} regionId="headline" errorElement={<OurStoryContent />} />
                ) : (
                    <OurStoryContent />
                )}
            </div>
        </CategoryHrefProvider>
    );
}
